import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { BooleanInput } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { User } from 'app/core/user/user.model';
import { UserService } from 'app/core/user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'app/core/auth/auth.service';
import { GAnalyticsService } from '../../../services/g-analytics.service';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'help-menu',
  templateUrl: './help-menu.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'userMenu2',
})
export class HelpMenuComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;

  @Input() showAvatar: boolean = true;
  user: User;
  isLoading = false;
  isArabic: boolean = false;

  @ViewChild('signOutDialog') signOutDialog;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  currentLang = localStorage.getItem('lang') || 'en';

  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private userService: UserService,
    private authService: AuthService,
    private dialog: MatDialog,
    private googleServices: GAnalyticsService,
    public transloco: TranslocoService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  async ngOnInit() {
    this.user = await this.userService.getCurrentUser();
    const language = this.transloco.getActiveLang();
    if (language === 'ar') {
      this.isArabic = true;
    } else {
      this.isArabic = false;
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(false);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the user status
   *
   * @param status
   */
  updateUserStatus(status: string): void {
    // Return if user is not available
    if (!this.user) {
      return;
    }

    this.userService.updateUser({
      ...this.user,
      status,
    });
  }

  /**
   * Sign out
   */
  signOutModal(): void {
    const dialogRef = this.dialog.open(this.signOutDialog, {
      maxWidth: '640px',
    });
  }

  signOut(type?): void {
    if (type === 'all') {
      this.googleServices.setEvent('SignOut_AllDevices_Settings', 'Authentication');
      this.isLoading = true;
      const LOGOUT_ALL_TIMEOUT_MS = 30000; // Prevent stuck UI for slow V1 API (PC-1632)
      this.authService
        .signOutFromAll()
        .pipe(
          timeout(LOGOUT_ALL_TIMEOUT_MS),
          catchError(() => of(null)), // On error or timeout, still clear and reload so user is not stuck
        )
        .subscribe(() => {
          this.isLoading = false;
          this.dialog.closeAll();
          // Clear token and reload so user is not left authenticated (fixes redirect loop / stuck for V1 - PC-1632)
          this.authService.clearLocalAuthAndReload();
        });
    } else {
      this.googleServices.setEvent('SignOut_CurrentDevice_Settings', 'Authentication');
      this._router.navigate(['/sign-out']);
      this.dialog.closeAll();
    }
  }

  switchLanguage(lang: string): void {
    if (lang === this.currentLang) {
      return;
    }

    this.googleServices.setEvent('LanguageSelection_Settings', 'Authentication');

    // Track specific language selection
    const langMap: { [key: string]: string } = {
      en: 'LanguageSelection_English_Settings',
      ar: 'LanguageSelection_Arabic_Settings',
      ru: 'LanguageSelection_Russian_Settings',
    };
    if (langMap[lang]) {
      this.googleServices.setEvent(langMap[lang], 'Authentication');
    }

    this.currentLang = lang;
    localStorage.setItem('lang', lang);
    window.location.reload();
  }

  // Keep old method for backward compatibility
  swtichLang(): void {
    const lang = this.currentLang === 'en' ? 'ar' : 'en';
    this.switchLanguage(lang);
  }
}
